
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import contentfulService from '@/services/contentful';
import contentfulVideoService from '@/services/contentfulVideoService';

export function useSearchLogic() {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ stories: [], videos: [] });
  const [includeStories, setIncludeStories] = useState(true);
  const [includeVideos, setIncludeVideos] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const location = useLocation();

  const { data: stories = [] } = useQuery({
    queryKey: ['stories'],
    queryFn: contentfulService.getStories,
  });

  const { data: videos = [] } = useQuery({
    queryKey: ['videos'],
    queryFn: contentfulVideoService.getVideos,
  });

  // Check for URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('q');
    const tag = params.get('tag');
    
    if (searchQuery) {
      setQuery(searchQuery);
    } else if (tag) {
      setQuery(tag);
    }
  }, [location.search]);

  // Perform search when query or filters change
  useEffect(() => {
    const performSearch = async () => {
      if (!query) {
        setSearchResults({ stories: [], videos: [] });
        return;
      }

      setIsSearching(true);
      const lowerQuery = query.toLowerCase();

      const filteredStories = includeStories ? stories.filter((story) =>
        story.title?.toLowerCase().includes(lowerQuery) ||
        story.excerpt?.toLowerCase().includes(lowerQuery) ||
        story.content?.toLowerCase().includes(lowerQuery) ||
        story.author?.name?.toLowerCase().includes(lowerQuery) ||
        story.category?.toLowerCase().includes(lowerQuery) ||
        (Array.isArray(story.tags) && story.tags.some((tag) =>
          tag.toLowerCase().includes(lowerQuery)
        ))
      ) : [];

      const filteredVideos = includeVideos ? videos.filter((video) =>
        video.title?.toLowerCase().includes(lowerQuery) ||
        video.description?.toLowerCase().includes(lowerQuery) ||
        (Array.isArray(video.genre) && video.genre.some((genre) =>
          genre.toLowerCase().includes(lowerQuery)
        )) ||
        (Array.isArray(video.tags) && video.tags.some((tag) =>
          tag.toLowerCase().includes(lowerQuery)
        ))
      ) : [];

      setSearchResults({ stories: filteredStories, videos: filteredVideos });
      setIsSearching(false);
    };

    performSearch();
  }, [query, stories, videos, includeStories, includeVideos]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults({ stories: [], videos: [] });
  };

  return {
    query,
    searchResults,
    isSearching,
    includeStories,
    includeVideos,
    handleSearchChange,
    clearSearch,
    setIncludeStories,
    setIncludeVideos
  };
}
